import { JOURNEY_VERSION } from "./journey";
import type { AnalyticsBooking, AnalyticsEcommercePayload, AnalyticsItem, AnalyticsVenue } from "./types";

/** Item category used by Collins bookings, matching the legacy dataLayer pushes. */
export const ITEM_CATEGORY = "Access Collins";

/** Fallback currency when the venue has not supplied one. */
export const DEFAULT_CURRENCY = "GBP";

export interface PreOrderItemInput {
  readonly id: string;
  readonly name: string;
  readonly price?: number;
  readonly quantity?: number;
}

function resolveCurrency(venue?: AnalyticsVenue, override?: string): string {
  return override ?? venue?.currency ?? DEFAULT_CURRENCY;
}

/** Builds the ecommerce item representing the booking itself. */
export function buildBookingItem(booking: AnalyticsBooking, venue?: AnalyticsVenue): AnalyticsItem {
  return {
    item_id: booking.typeId ?? booking.id ?? "",
    item_name: booking.typeName ?? "Booking",
    item_category: ITEM_CATEGORY,
    item_brand: venue?.title ?? "",
    quantity: booking.numPeople ?? 1,
  };
}

/** `widget_loaded` — the widget finished loading its venue configuration. */
export function buildWidgetLoadedPayload(venue?: AnalyticsVenue): AnalyticsEcommercePayload {
  return {
    currency: resolveCurrency(venue),
    value: 0,
    journey_version: JOURNEY_VERSION,
    ...(venue?.id ? { venue_id: venue.id } : {}),
    items: [],
  };
}

/**
 * The funnel selection payload, shared by the `select_*` events and
 * `begin_checkout` so a report can compare the steps field by field.
 *
 * Only what the funnel actually holds at that point is included: the venue and
 * currency come from its configuration, the booking type, party size, date and
 * time from the guest's own choices so far.
 */
export function buildFunnelSelectionPayload(
  booking: AnalyticsBooking,
  venue?: AnalyticsVenue,
): AnalyticsEcommercePayload {
  return {
    currency: resolveCurrency(venue, booking.currency),
    value: booking.value ?? 0,
    journey_version: JOURNEY_VERSION,
    ...(venue?.id ? { venue_id: venue.id } : {}),
    ...(booking.date ? { booking_date: booking.date } : {}),
    ...(booking.time ? { booking_time: booking.time } : {}),
    items: booking.typeId || booking.typeName ? [buildBookingItem(booking, venue)] : [],
  };
}

/** `begin_checkout` — a pending booking exists and the guest is entering details. */
export function buildBeginCheckoutPayload(
  booking: AnalyticsBooking,
  venue?: AnalyticsVenue,
): AnalyticsEcommercePayload {
  return buildFunnelSelectionPayload(booking, venue);
}

/** `add_to_cart` / `remove_from_cart` — a pre-order item quantity changed. */
export function buildCartPayload(
  item: PreOrderItemInput,
  venue?: AnalyticsVenue,
  currency?: string,
): AnalyticsEcommercePayload {
  const quantity = item.quantity ?? 1;
  const price = item.price ?? 0;

  return {
    currency: resolveCurrency(venue, currency),
    value: Number((price * quantity).toFixed(2)),
    journey_version: JOURNEY_VERSION,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: ITEM_CATEGORY,
        item_brand: venue?.title ?? "",
        price,
        quantity,
      },
    ],
  };
}

/**
 * The booking as a single transaction line, keyed by the booking id.
 *
 * `payment_journey_id` is carried when the caller knows it, so `payment_initiated`
 * and `purchase` can be matched even where the final booking id differs from the
 * pending one it was initiated against.
 */
function buildTransactionPayload(booking: AnalyticsBooking, venue?: AnalyticsVenue): AnalyticsEcommercePayload {
  return {
    currency: resolveCurrency(venue, booking.currency),
    value: booking.value ?? 0,
    journey_version: JOURNEY_VERSION,
    transaction_id: booking.id,
    ...(booking.journeyId ? { payment_journey_id: booking.journeyId } : {}),
    items: [buildBookingItem(booking, venue)],
  };
}

/** `payment_initiated` — the guest submitted the payment step. */
export function buildPaymentInitiatedPayload(
  booking: AnalyticsBooking,
  venue?: AnalyticsVenue,
): AnalyticsEcommercePayload {
  return buildTransactionPayload(booking, venue);
}

/** `purchase` — a booking happened. Fired regardless of who owns the guest. */
export function buildPurchasePayload(booking: AnalyticsBooking, venue?: AnalyticsVenue): AnalyticsEcommercePayload {
  return buildTransactionPayload(booking, venue);
}
